import React, { FC, memo } from 'react';
import classNames from 'classnames';
import { history, useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import TextBubbles from '@home/components/TextBubbles';
import BasicImage from '@home/components/BasicImage';
import HighlightText from '@/pages/aigc/components/HighlightText';
import { useCopilotOpenBridge } from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';
import { IAgentData } from '../../types';
import AgentRightArrow from '../../../svg/agent-right-arrow.svg';
import AgentSuccessIcon from '../../../svg/agent-success-color.svg';
import AgentModeDefaultIcon from '../../../svg/agent-mode-default-icon.svg';
import AgentModeAuthorDefaultIcon from '../../../svg/agent-mode-author-default-icon.svg';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';

interface IAgentCardProps {
  /** Agent 数据 */
  agent: IAgentData;
  /** 是否选中 */
  isSelected?: boolean;
  /** 搜索关键词（用于高亮） */
  searchKeyword?: string;
}

/**
 * Agent 卡片组件
 */
const AgentCard: FC<IAgentCardProps> = ({ agent, isSelected = false, searchKeyword = '' }) => {
  const { locale, formatMessage } = useIntl();
  const { openCopilotWithAgentId } = useCopilotOpenBridge();
  const isEN = locale === LANGUAGE_EN;
  // 获取描述文本(根据语言)
  const description = isEN ? agent.description : agent.description_cn;
  // 是否有权限
  const hasPermission = agent?.hasPermission !== false;

  const goHomeWithAgent = (): void => {
    if (!hasPermission) {
      return;
    }
    openCopilotWithAgentId([agent.agentId]);
    history.push('/agent/home');
  };

  const handleClick = () => {
    goHomeWithAgent();
  };
  const handleTryNowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    goHomeWithAgent();
  };

  return (
    <div
      className={classNames('dba-agent-card', {
        'dba-agent-card--selected': isSelected,
        'dba-agent-card--no-permission': !hasPermission,
      })}
      onClick={handleClick}
    >
      {/* "当前使用"标签 */}
      {isSelected && (
        <div className="dba-agent-card__current-badge">
          <img src={AgentSuccessIcon} alt="" />
          <div>{formatMessage({ id: 'dba_collection_card_in_use' })}</div>
        </div>
      )}

      {/* "暂无权限"标签 */}
      {!hasPermission && (
        <div className="dba-agent-card__no-permission-badge">
          {formatMessage({ id: 'dba_collection_card_no_access' })}
        </div>
      )}

      {/* 顶部区域:图标 + 标题 */}
      <div className="dba-agent-card__header">
        <div className="dba-agent-card__icon">
          <BasicImage
            src={agent.icon}
            alt={agent.name}
            errorSrc={AgentModeDefaultIcon}
          />
          {agent.isDemo && (
            <span className="dba-agent-card__demo-badge">
              <SvgIcon type="beta" />
            </span>
          )}
        </div>

        <div className="dba-agent-card__name">
          <HighlightText
            name={isEN ? agent.name : agent.name_cn}
            keyword={searchKeyword}
          />
        </div>
      </div>

      {/* 内容 */}
      <div className="dba-agent-card__content">
        {/* 描述 - 最多显示2行,超出显示 tooltip */}
        <TextBubbles
          text={<HighlightText name={description} keyword={searchKeyword} />}
          rowNum={2}
          placement="top"
          className="dba-agent-card__description"
        />

        {/* 底部作者信息和按钮 */}
        <div className="dba-agent-card__footer">
          <div className="dba-agent-card__author">
            <BasicImage
              src={agent.authorAvatar}
              alt={agent.author}
              className="dba-agent-card__author-avatar"
              errorSrc={AgentModeAuthorDefaultIcon}
            />
            <span className="dba-agent-card__author-name">{agent.author}</span>
          </div>

          {/* Hover 时显示的按钮 / 无权限时显示提示文字 */}
          <div className="dba-agent-card__action">
            {hasPermission ? (
              <div
                className="dba-agent-card__action-btn"
                onClick={handleTryNowClick}
              >
                {formatMessage({ id: 'dba_collection_card_try_now' })}
                <img src={AgentRightArrow} alt="" />
              </div>
            ) : (
              <div className="dba-agent-card__no-permission-text">
                {formatMessage({ id: 'dba_collection_card_no_permission_text' })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(AgentCard);
